import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';

import { Purchase, PurchasePriorityLevel } from './purchase.model';
import { EnumTextValuesPipe } from '../../shared/pipes/enum-keys-text.pipe';
import { EnumKeysPipe } from '../../shared/pipes/enum-keys.pipe';
import { Task } from '../../shared/tasks/task.model';
import { Attachment } from '../../shared/attachments/attachment.model';
import { Company } from '../companies/company.model';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class PurchaseService implements OnDestroy {

  keepSubscriptionsAlive: boolean = true;

  /* used for the role options enum */
  purchaseInterestOptions: any;
  purchaseInterestDropDownOptions: any;
  purchasePriorityLevel = PurchasePriorityLevel;
  purchasePriorityLevelOptions: any;
  purchasePriorityLevelDropDownOptions: any;

  constructor(private http: HttpClient,
    private toastrService: ToastrService,
    private enumTextValuesPipe: EnumTextValuesPipe,
    private enumKeyValuesPipe: EnumKeysPipe
  ) { }

  ngOnDestroy() {
    this.keepSubscriptionsAlive = false;
  }

  setPriorityLevelOptions() { this.purchasePriorityLevelOptions = this.enumTextValuesPipe.transform(this.purchasePriorityLevel, ['']); }
  setPriorityLevelDropDownOptions() { this.purchasePriorityLevelDropDownOptions = this.enumKeyValuesPipe.transform(this.purchasePriorityLevel, ['']); }

  addTaskToPurchase(purchase: Purchase, task: Task) {

    // create a spoof company and use the id - otherwise we get a json circular reference
    const spoofTask = new Task();
    spoofTask._id = task._id;

    // push the spoofed company to the team member
    purchase.tasks.unshift(spoofTask);

    // console.log('saving company to teamMember');
    this.savePurchase(purchase);

  }

  addAttachmentToPurchase(purchase: Purchase, attachment: Attachment) {

    // create a spoof company and use the id - otherwise we get a json circular reference
    const spoofAttachment = new Attachment();
    spoofAttachment._id = attachment._id;

    // push the spoofed company to the team member
    purchase.attachments.unshift(spoofAttachment);

    // console.log('saving company to teamMember');
    this.savePurchase(purchase);

  }

  savePurchase(purchase: Purchase) {

    this.editPurchase(purchase)
      .takeWhile(() => this.keepSubscriptionsAlive)
      .subscribe(
        res => {
          // console.log(res);
        },
        error => {
          this.toastrService.error('There was an issue saving the Potential Purchase data.');
          console.log(error);
        }
      );

  }

  getPurchases(): Observable<Purchase[]> {
    return this.http.get<Purchase[]>('/api/purchases');
  }

  getPurchasesByPurchaser(companyId: string): Observable<Purchase[]> {
    return this.http.get<Purchase[]>(`/api/purchases/${companyId}`, {
      headers: { ignoreLoadingBar: '' }
    }).map((purchases: Purchase[]) => {
      return purchases;
    }).catch(this.handleError);

  }

  findTopNPurchases(searchString: string, limit: number = 5): Observable<Purchase[]> {

    if (searchString === undefined || searchString === '') { searchString = ' '; }

    return this.http.get<Purchase[]>(`/api/purchases/find/top/${searchString}/${limit}`, {
      headers: { ignoreLoadingBar: '' }
    }).map((purchases: Purchase[]) => {
      return purchases;
    }).catch(this.handleError);

  }

  searchPurchases(searchString: string): Observable<Purchase[]> {

    // see https://github.com/surfer77/mongoose-string-query for full API!
    return this.http.get<Purchase[]>('/api/purchases/search?' + searchString, {
      // params: { ignoreLoadingBar: 'true' }
      headers: { ignoreLoadingBar: '' }
    }).map((purchases: Purchase[]) => {
      return purchases;
    }).catch(this.handleError);

  }

  countPurchases(): Observable<number> {
    return this.http.get<number>('/api/purchases/count');
  }

  addPurchase(purchase: Purchase): Observable<Purchase> {
    // console.log(purchase);
    return this.http.post<Purchase>('/api/purchase', purchase);
  }

  getPurchaseFromId(id: string): Observable<Purchase> {
    return this.http.get<Purchase>(`/api/purchase/${id}`);
  }

  getPurchase(purchase: Purchase): Observable<Purchase> {
    return this.http.get<Purchase>(`/api/purchase/${purchase._id}`);
  }
  
  getPurchaseFromClientAndPurchaser(client: Company, purchaser: Company): Observable<Purchase> {
    return this.http.get<Purchase>(`/api/purchase/clientAndPurchaser/${client._id}/${purchaser._id}`, {
      headers: { ignoreLoadingBar: '' }
    });
  }

  editPurchase(purchase: Purchase): Observable<string> {
    return this.http.put(`/api/purchase/${purchase._id}`, purchase, { responseType: 'text' });
  }

  deletePurchase(purchase: Purchase): Observable<string> {
    return this.http.delete(`/api/purchase/${purchase._id}`, { responseType: 'text' });
  }

  private handleError(error: Response) {
    return Observable.throw(error.statusText);
  }

}



// WEBPACK FOOTER //
// ./client/app/features/projects/purchase.service.ts